import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Logo } from '@/components/Logo';
import { useToast } from '@/components/toast-context';
import { buttonClass } from '@/components/styles';
import { ErrorNote, Spinner } from '@/components/ui';
import { markOnboardingDone } from '@/features/onboarding/state';
import { takePending } from './connect';

/**
 * The OAuth return page: `/connections/callback?connection_id=..&status=connected|needs_account|error&message=..`.
 */
export function ConnectCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const handled = useRef(false);
  const [error, setError] = useState<{ message: string; next: string } | null>(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const pending = takePending();
    const next = pending?.next ?? '/';
    const id = params.get('connection_id');
    const status = params.get('status');
    const message = params.get('message');
    void qc.invalidateQueries();

    if (status === 'connected' && id) {
      if (pending) markOnboardingDone(pending.integration_id);
      toast({ title: 'Connected', description: 'Your numbers will appear in a moment.' });
      navigate(next, { replace: true });
    } else if (status === 'needs_account' && id) {
      navigate(`/connections/${id}/account?next=${encodeURIComponent(next)}`, { replace: true });
    } else {
      setError({ message: message || 'The app didn’t finish connecting. Please try again.', next });
    }
  }, [navigate, params, qc, toast]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4">
      <Logo className="h-8" />
      {error ? (
        <div className="w-full max-w-md">
          <ErrorNote title="Couldn’t connect" message={error.message} />
          <div className="mt-4 flex gap-2">
            <Link to="/integrations" className={buttonClass('primary')}>
              Try again
            </Link>
            <Link to={error.next} className={buttonClass('secondary')}>
              {error.next === '/onboarding' ? 'Back to setup' : 'Back to the dashboard'}
            </Link>
          </div>
        </div>
      ) : (
        <Spinner label="Finishing the connection" />
      )}
    </div>
  );
}
